import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import {
	NEWSPAPER_APPROVAL_START,
	APPROVE_NEWSPAPER_START,
	REJECT_NEWSPAPER_START,
	DELETE_NEWSPAPER_START,
	BULK_APPROVE_NEWSPAPER_START,
} from './NewspaperApproval.types';
import {
	newspaperApproval,
	approveNewspaper,
	rejectNewspaper,
	bulkApproveNewspaper,
	deleteNewspaper,
} from './NewspaperApproval.services';
import {
	newspaperApprovalSuccess,
	newspaperApprovalFailure,
	approveNewspaperSuccess,
	approveNewspaperFailure,
	rejectNewspaperSuccess,
	rejectNewspaperFailure,
	bulkApproveNewspaperSuccess,
	bulkApproveNewspaperFailure,
	deleteNewspaperSuccess,
	deleteNewspaperFailure,
} from './NewspaperApproval.actions';
import AlertManager from '../../utils/alertManager';
import { encryptData } from '../_common/enode-decode';

export function* fetchNewspaperApprovalSaga(payload: any) {
	try {
		const key = 'fetchNewspaperApprovalKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const responseData = yield call(newspaperApproval as any, encryptedData);

		// Handle the response data - it might contain HTML mixed with JSON
		let parsedData = responseData.data;

		// If the response is a string, try to extract JSON from it
		if (typeof responseData.data === 'string') {
			try {
				// Try to find JSON in the response string using a different approach
				const jsonStart = responseData.data.indexOf('{');
				const jsonEnd = responseData.data.lastIndexOf('}');
				if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
					const jsonString = responseData.data.substring(jsonStart, jsonEnd + 1);
					parsedData = JSON.parse(jsonString);
				}
			} catch (parseError) {
				console.error('Failed to parse JSON from response:', parseError);
			}
		}

		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			// Check if we have valid data
			if (parsedData && parsedData.status === 'success' && parsedData.data) {
				yield put(newspaperApprovalSuccess(parsedData));
			} else if (parsedData && parsedData.data && Array.isArray(parsedData.data)) {
				// If data is directly an array, wrap it in success format
				yield put(
					newspaperApprovalSuccess({
						status: 'success',
						message: 'Newspaper allowances fetched successfully',
						data: parsedData.data,
					}),
				);
			} else {
				yield put(newspaperApprovalFailure(parsedData || 'Invalid response format'));
			}
		}
	} catch (error: any) {
		console.error('Saga error:', error);
		yield put(
			newspaperApprovalFailure(
				error?.response?.data?.message || 'Error fetching newspaper allowance',
			),
		);
	}
}

export function* approveNewspaperSaga(payload: any) {
	try {
		const key = 'approveNewspaperKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const responseData = yield call(approveNewspaper as any, encryptedData);

		let parsedData = responseData.data;

		if (typeof responseData.data === 'string') {
			try {
				const jsonStart = responseData.data.indexOf('{');
				const jsonEnd = responseData.data.lastIndexOf('}');
				if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
					const jsonString = responseData.data.substring(jsonStart, jsonEnd + 1);
					parsedData = JSON.parse(jsonString);
				}
			} catch (parseError) {
				console.error('Failed to parse JSON from response:', parseError);
			}
		}
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (parsedData && parsedData.status === 'success') {
				yield put(approveNewspaperSuccess(parsedData));
			} else {
				yield put(approveNewspaperFailure(parsedData || 'Invalid response format'));
			}
		}
	} catch (error: any) {
		console.error('Saga error:', error);
		yield put(
			approveNewspaperFailure(
				error?.response?.data?.message || 'Error approving newspaper allowance',
			),
		);
	}
}

export function* rejectNewspaperSaga(payload: any) {
	try {
		const key = 'rejectNewspaperKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const responseData = yield call(rejectNewspaper as any, encryptedData);

		let parsedData = responseData.data;

		if (typeof responseData.data === 'string') {
			try {
				const jsonStart = responseData.data.indexOf('{');
				const jsonEnd = responseData.data.lastIndexOf('}');
				if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
					const jsonString = responseData.data.substring(jsonStart, jsonEnd + 1);
					parsedData = JSON.parse(jsonString);
				}
			} catch (parseError) {
				console.error('Failed to parse JSON from response:', parseError);
			}
		}
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (parsedData && parsedData.status === 'success') {
				yield put(rejectNewspaperSuccess(parsedData));
			} else {
				yield put(rejectNewspaperFailure(parsedData || 'Invalid response format'));
			}
		}
	} catch (error: any) {
		console.error('Saga error:', error);
		yield put(
			rejectNewspaperFailure(
				error?.response?.data?.message || 'Error rejecting newspaper allowance',
			),
		);
	}
}

export function* bulkApproveNewspaperSaga(payload: any) {
	try {
		const key = 'bulkApproveNewspaperKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const responseData = yield call(bulkApproveNewspaper as any, encryptedData);

		let parsedData = responseData.data;

		if (typeof responseData.data === 'string') {
			try {
				const jsonStart = responseData.data.indexOf('{');
				const jsonEnd = responseData.data.lastIndexOf('}');
				if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
					const jsonString = responseData.data.substring(jsonStart, jsonEnd + 1);
					parsedData = JSON.parse(jsonString);
				}
			} catch (parseError) {
				console.error('Failed to parse JSON from response:', parseError);
			}
		}
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (parsedData && parsedData.status === 'success') {
				yield put(bulkApproveNewspaperSuccess(parsedData));
			} else {
				yield put(bulkApproveNewspaperFailure(parsedData || 'Invalid response format'));
			}
		}
	} catch (error: any) {
		console.error('Saga error:', error);
		yield put(
			bulkApproveNewspaperFailure(
				error?.response?.data?.message || 'Error bulk approving newspaper allowances',
			),
		);
	}
}

export function* deleteNewspaperSaga(payload: any) {
	try {
		const key = 'deleteNewspaperKey';
		const encryptedData = encryptData(payload.payload, key);
		// @ts-ignore
		const responseData = yield call(deleteNewspaper as any, encryptedData);

		let parsedData = responseData.data;

		if (typeof responseData.data === 'string') {
			try {
				const jsonStart = responseData.data.indexOf('{');
				const jsonEnd = responseData.data.lastIndexOf('}');
				if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
					const jsonString = responseData.data.substring(jsonStart, jsonEnd + 1);
					parsedData = JSON.parse(jsonString);
				}
			} catch (parseError) {
				console.error('Failed to parse JSON from response:', parseError);
			}
		}
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (parsedData && parsedData.status === 'success') {
				yield put(deleteNewspaperSuccess(parsedData));
			} else {
				yield put(deleteNewspaperFailure(parsedData || 'Invalid response format'));
			}
		}
	} catch (error: any) {
		console.error('Saga error:', error);
		yield put(
			deleteNewspaperFailure(
				error?.response?.data?.message || 'Error deleting newspaper allowance',
			),
		);
	}
}

export function* onNewspaperApprovalStart(): Generator<any, void, any> {
	yield takeLatest(NEWSPAPER_APPROVAL_START as any, fetchNewspaperApprovalSaga);
}

export function* onApproveNewspaperStart(): Generator<any, void, any> {
	yield takeLatest(APPROVE_NEWSPAPER_START as any, approveNewspaperSaga);
}

export function* onRejectNewspaperStart(): Generator<any, void, any> {
	yield takeLatest(REJECT_NEWSPAPER_START as any, rejectNewspaperSaga);
}

export function* onBulkApproveNewspaperStart(): Generator<any, void, any> {
	yield takeLatest(BULK_APPROVE_NEWSPAPER_START as any, bulkApproveNewspaperSaga);
}

export function* onDeleteNewspaperStart(): Generator<any, void, any> {
	yield takeLatest(DELETE_NEWSPAPER_START as any, deleteNewspaperSaga);
}

export function* newspaperApprovalSaga(): Generator<any, void, any> {
	yield all([
		fork(onNewspaperApprovalStart),
		fork(onApproveNewspaperStart),
		fork(onRejectNewspaperStart),
		fork(onDeleteNewspaperStart),
		fork(onBulkApproveNewspaperStart),
	]);
}
